import MODELS from '../config/models';

export async function getEphemeralToken() {
    try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/openai/realtimeToken`)
        console.log("response is:", response);
        const data = await response.json();
        console.log("[Realtime] 📩 Token received:", data);

        let ephemeralKey = data.client_secret.value;
        let selectedVoice = data.voice || "nova";

        return { ephemeralKey, selectedVoice };

    } catch (error) {
        console.error("[Realtime] ❌ Failed to get token:", error);
        return null;
    }
}

export async function setupAudioElement({ audioElementRef, isAudioPlaybackEnabled }) {
    if (!audioElementRef.current) {
        const audio = document.createElement("audio");
        audio.autoplay = true;
        audio.muted = false;
        audio.controls = false;
        audio.style.display = "none";

        document.body.appendChild(audio);
        audioElementRef.current = audio;
    }

    audioElementRef.current.autoplay = isAudioPlaybackEnabled;

    audioElementRef.current
        .play()
        .catch((err) => {
            console.warn("[Audio] ⚠️ Autoplay might be blocked by browser:", err);
        });

    console.log("[Audio] ✅ Audio element setup complete");
}

export async function createRealtimeConnection({
    ephemeralKey,
    audioElementRef,
    isMicOn
}) {
    const peerConnection = new RTCPeerConnection();

    peerConnection.ontrack = (event) => {
        if (audioElementRef.current) {
            audioElementRef.current.srcObject = event.streams[0];
            console.log("[Realtime] ✅ Realtime connection established");
        }
    }

    const localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    localStream.getTracks().forEach((track) => {
        track.enabled = isMicOn;
        peerConnection.addTrack(track, localStream);
    });

    const dataChannel = peerConnection.createDataChannel("oai-events");

    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);

    const baseUrl = "https://api.openai.com/v1/realtime/calls";
    const model = MODELS.REALTIME;

    const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
        method: "POST",
        body: offer.sdp,
        headers: {
            Authorization: `Bearer ${ephemeralKey}`,
            "Content-Type": "application/sdp",
        },
    });

    const sdpAnswer = await sdpResponse.text();
    const answer = new RTCSessionDescription({
        type: "answer",
        sdp: sdpAnswer,
    });

    await peerConnection.setRemoteDescription(answer);

    console.log("[Realtime] ✅ Realtime connection established");

    return {
        peerConnection,
        dataChannel,
        localStream,
    };
}



export async function connectRealtime({
    systemPrompt,
    audioElementRef,
    isMicOn,
    isAudioPlaybackEnabled,
    onServerEvent,
}) {
    console.log("[Realtime] Starting connection...");
    const tokenInfo = await getEphemeralToken();
    if (!tokenInfo) {
        return;
    }

    const { ephemeralKey, selectedVoice } = tokenInfo;

    setupAudioElement({ audioElementRef, isAudioPlaybackEnabled });

    const { peerConnection, dataChannel, localStream } = await createRealtimeConnection({
        ephemeralKey,
        audioElementRef,
        isMicOn,
    });

    if (dataChannel) {
        let sessionCreated = false;
        dataChannel.addEventListener("message", (event) => {
            const message = JSON.parse(event.data);
            console.log("[Realtime] 📩 Event received:", message.type);
            if (message.type === "error") {
                console.error("[Realtime] 🔴 Server error:", JSON.stringify(message.error, null, 2));
            }
            if (message.type === "session.updated" && !sessionCreated) {
                console.log("[Realtime] 🚀 Session updated, great start");
                sessionCreated = true;
        
                if (dataChannel.readyState === "open" ) {
                  const initialMsg = {
                    type: "conversation.item.create",
                    item: {
                      type: "message",
                      role: "user",
                      content: [{ type: "input_text", text: "hi" }]
                    }
                  };
                  dataChannel.send(JSON.stringify(initialMsg));
                  console.log("[Realtime] 🗣️ Sent initial assistant message AFTER session.updated");
        
                  const responseTrigger = {
                    type: "response.create"
                  };
                
                  
                  dataChannel.send(JSON.stringify(responseTrigger));
                    
                }
            }
            onServerEvent?.(message);
        });

        dataChannel.addEventListener("open", () => {
            console.log("[Realtime] 🔌 Data channel opened");

            const sessionUpdateEvent = {
                type: "session.update",
                session: {
                  type: "realtime",
                  output_modalities: ["audio"],
                  instructions: systemPrompt,
                  audio: {
                    input: {
                      transcription: { model: "whisper-1" },
                      turn_detection: {
                        type: "server_vad",
                        create_response: true,
                        silence_duration_ms: 500,
                        prefix_padding_ms: 300,
                        threshold: 0.5
                      }
                    }
                  }
                }
            };

            dataChannel.send(JSON.stringify(sessionUpdateEvent));
            console.log("[Realtime] ✅ Sent session.update with system prompt");
        });


        dataChannel.addEventListener("close", () => {
            console.log("[Realtime] 🔌 Data channel closed");
        });

        dataChannel.addEventListener("error", (event) => {
            console.error("[Realtime] 🔴 Data channel error:", event);
        });
    }

    return {
        peerConnection,
        dataChannel,
        localStream,
    };
}